<template>
  <!--
    WHAT — Structured Analysis detail tab "Overview". Configures the analysis's
      description, active flag, the EXECUTOR (agent vs patient_dossier), and —
      for the agent executor only — its cohort: the population DataAttribute
      (config.population_da_id) and the unit entity type (config.unit_entity_type).

    CONTRACT — emits via TWO paths:
      • emitMeta() → patch({ description, is_active })  (record-level scalars)
      • emitConfig() → patch({ config }) where config is a SHALLOW COPY of
        record.config. The executor picker writes config.executor; the cohort
        selects write population_da_id + unit_entity_type. Switching to
        patient_dossier seeds pool/model/subgraph defaults so the Dossier tab
        hydrates cleanly; switching to agent leaves keys untouched (the Dossier
        tab's keys are harmless to the agent runner, which ignores them).

    NOTES — hydrating guard as elsewhere. The executor choice REDRAWS THE TABS
      (registry sections are a function of config.executor) — ArtifactDetail
      re-validates the active tab, so picking Patient Dossier here immediately
      reveals the Dossier / Prompt / Output set.
  -->
  <div class="space-y-5">
    <!-- Executor — chooses the entire shape of the analysis. -->
    <div>
      <label class="block text-[11px] font-medium text-gray-500 mb-1.5">Executor</label>
      <div class="grid grid-cols-2 gap-3">
        <button
          type="button"
          class="text-left px-3 py-2 border rounded-lg transition-colors"
          :class="executor === 'agent' ? 'border-blue-400 bg-blue-50' : 'border-gray-200 bg-white hover:bg-gray-50'"
          @click="setExecutor('agent')"
        >
          <div class="text-xs font-semibold text-gray-800">Agent · discovery</div>
          <div class="text-[10px] text-gray-500 mt-0.5 leading-snug">Multi-turn agent over Snowflake DataAttribute primitives. Finds outliers across a cohort of entities.</div>
        </button>
        <button
          type="button"
          class="text-left px-3 py-2 border rounded-lg transition-colors"
          :class="executor === 'patient_dossier' ? 'border-blue-400 bg-blue-50' : 'border-gray-200 bg-white hover:bg-gray-50'"
          @click="setExecutor('patient_dossier')"
        >
          <div class="text-xs font-semibold text-gray-800">Patient Dossier · narration</div>
          <div class="text-[10px] text-gray-500 mt-0.5 leading-snug">One structured LLM call per patient over that patient's Axe subgraph. Pre-computed, batch, per-patient.</div>
        </button>
      </div>
    </div>

    <div>
      <label class="block text-[11px] font-medium text-gray-500 mb-1">Description</label>
      <input
        v-model="description" placeholder="Short description…"
        class="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400" @input="emitMeta" />
    </div>

    <!-- Population — BOTH executors use a population DataAttribute. Agent runs
         its primitives over this cohort of entities; dossier runs one call per
         patient the DA emits (the DA must emit BENE_MBI_ID for dossier).
         Entity type is agent-only (the finding grain). -->
    <div class="grid grid-cols-2 gap-4">
      <div>
        <label class="block text-[11px] font-medium text-gray-500 mb-1">Population</label>
        <select v-model.number="populationDaId" class="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-blue-400" @change="emitConfig">
          <option :value="null">—</option>
          <option v-for="p in populations" :key="p.id" :value="p.id">{{ p.name }} · #{{ p.id }}</option>
        </select>
      </div>
      <div v-if="executor === 'agent'">
        <label class="block text-[11px] font-medium text-gray-500 mb-1">Entity type</label>
        <select v-model="unitEntityType" class="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-blue-400" @change="emitConfig">
          <option value="">—</option>
          <option v-for="et in entityTypes" :key="et" :value="et">{{ et }}</option>
        </select>
      </div>
      <!-- Model — DOSSIER ONLY. The agent's model is fixed by its job.yaml. -->
      <div v-if="executor === 'patient_dossier'">
        <label class="block text-[11px] font-medium text-gray-500 mb-1">Model</label>
        <input
v-model="model" type="text" placeholder="gpt-5.6-luna"
          class="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-blue-400" @input="emitConfig" />
      </div>
    </div>

    <!-- Patient graph — DOSSIER ONLY. The sole data source for the per-patient call. -->
    <div v-if="executor === 'patient_dossier'" class="space-y-3">
      <label class="flex items-start gap-2.5 text-xs text-gray-700">
        <input v-model="usePatientGraph" type="checkbox" class="rounded mt-0.5" @change="emitConfig" />
        <span>
          <span class="font-medium text-gray-800">Use patient graph</span>
          <span class="block text-[10px] text-gray-400 mt-0.5 leading-snug">
            Feed the model the patient's graph as the context for the prompt.
            Choose which parts below — fewer parts = smaller, cheaper calls.
          </span>
        </span>
      </label>

      <!-- Node selection — which slices of the graph to send. The token lever. -->
      <div v-if="usePatientGraph" class="pl-6">
        <label class="block text-[11px] font-medium text-gray-500 mb-1.5">Graph data to send</label>
        <div class="grid grid-cols-2 gap-x-6 gap-y-1.5">
          <label v-for="n in graphNodeCatalog" :key="n.key" class="flex items-start gap-2 text-[11px] text-gray-700">
            <input
              type="checkbox"
              class="rounded mt-0.5"
              :checked="graphNodes.includes(n.key)"
              @change="toggleNode(n.key, $event.target.checked)"
            />
            <span>{{ n.label }}</span>
          </label>
        </div>
      </div>

      <!-- Data packets — extra DA-backed context blocks alongside the graph.
           Each DA (role='dossier') is run scoped to the patient and fed to the
           model as its own labeled packet. filters (optional JSON) supply the
           DA's optional clauses, values pulled from run_context at fire time
           (e.g. {"episode_id": "{episode_id}"}). -->
      <div class="pt-1">
        <div class="flex items-center justify-between mb-1.5">
          <label class="block text-[11px] font-medium text-gray-500">Data packets</label>
          <button type="button" class="text-[11px] text-blue-600 hover:text-blue-800 font-medium" @click="addPacket">+ Add packet</button>
        </div>
        <p class="text-[10px] text-gray-400 mb-2 leading-snug">
          Extra DA-backed context fed to the model alongside the graph. Each DA (role “dossier”) is run scoped to this patient.
        </p>
        <div v-if="!dataPackets.length" class="text-[11px] text-gray-400 italic mb-1">No data packets — graph only.</div>
        <div v-for="(pkt, idx) in dataPackets" :key="idx" class="border border-gray-200 rounded-lg p-2.5 mb-2 bg-gray-50/50 space-y-2">
          <div class="flex gap-2 items-start">
            <select v-model.number="pkt.da_id" class="flex-1 text-xs px-2 py-1.5 border border-gray-200 rounded bg-white focus:outline-none focus:border-blue-400" @change="emitConfig">
              <option :value="null">Select dossier DA…</option>
              <option v-for="d in dossierDAs" :key="d.id" :value="d.id">{{ d.name }} · #{{ d.id }}</option>
            </select>
            <input v-model="pkt.label" type="text" placeholder="label" class="w-32 text-xs px-2 py-1.5 border border-gray-200 rounded font-mono focus:outline-none focus:border-blue-400" @input="emitConfig" />
            <button type="button" class="w-6 h-6 mt-0.5 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded" @click="removePacket(idx)">×</button>
          </div>
          <div>
            <label class="block text-[10px] font-medium text-gray-500 mb-0.5">Filters (JSON, optional)</label>
            <textarea
              style="font-family:inherit"
              :value="pkt._filtersStr"
              rows="2" spellcheck="false"
              placeholder='{"episode_id": "{episode_id}"}'
              class="w-full text-[11px] px-2 py-1 border border-gray-200 rounded font-mono bg-white focus:outline-none focus:border-blue-400 resize-vertical"
              :class="pkt._filtersErr ? 'border-red-400' : ''"
              @input="onFiltersInput(pkt, $event.target.value)"
            ></textarea>
            <p v-if="pkt._filtersErr" class="text-[10px] text-red-500 mt-0.5">{{ pkt._filtersErr }}</p>
          </div>
        </div>
      </div>

      <!-- Cadence + scope. WHEN the batch fires (schedule) and WHICH patients
           within a fire (recompute). Change-driven is the default and the point:
           only rebuild a patient whose clinical data actually changed. -->
      <div class="grid grid-cols-2 gap-4 pt-1">
        <div>
          <label class="block text-[11px] font-medium text-gray-500 mb-1">Schedule</label>
          <select v-model="schedule" class="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-blue-400" @change="emitConfig">
            <option value="">On-demand only</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
          <p class="text-[10px] text-gray-400 mt-1 leading-snug">
            The scheduler fires this SAT on the chosen cadence (one batch run per contract below). Recompute still gates which patients rebuild within a run.
          </p>
        </div>
        <div>
          <label class="block text-[11px] font-medium text-gray-500 mb-1">Recompute</label>
          <select v-model="recompute" class="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-blue-400" @change="emitConfig">
            <option value="changed">Only patients with new clinical data</option>
            <option value="always">All patients every run</option>
          </select>
          <p class="text-[10px] text-gray-400 mt-1 leading-snug">
            Change = new claim / hospital episode / lab / med / encounter since the patient's last dossier.
          </p>
        </div>
      </div>

      <!-- Contracts — the scheduler spawns ONE batch run per contract listed
           here. Leave empty for a single, contract-agnostic run. -->
      <div v-if="schedule">
        <label class="block text-[11px] font-medium text-gray-500 mb-1">Contracts <span class="text-gray-400 font-normal">(comma-separated; empty = all)</span></label>
        <input
          v-model="contractsStr" type="text" placeholder="A4592, A5300 (leave blank for one run)"
          class="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-lg font-mono bg-white focus:outline-none focus:border-blue-400" @input="emitConfig" />
        <p class="text-[10px] text-gray-400 mt-1 leading-snug">
          One scheduled batch run per contract. The population DA is scoped to each contract in turn.
        </p>
      </div>
    </div>

    <label class="flex items-center gap-2 text-xs text-gray-700">
      <input v-model="isActive" type="checkbox" class="rounded" @change="emitMeta" /> Active
    </label>
  </div>
</template>

<script>
import axios from 'axios';

// The patient-graph node catalog, mirrored from api/patient_graph.py NODE_ORDER
// + NODE_LABELS. Keep in sync if the backend catalog changes.
const GRAPH_NODE_CATALOG = [
  { key: 'claims', label: 'Claims (diagnoses, providers, facilities, drugs)' },
  { key: 'ehr_medications', label: 'EHR medications' },
  { key: 'observations', label: 'Observations (labs, vitals)' },
  { key: 'encounters', label: 'EHR encounters' },
  { key: 'adt_episodes', label: 'ADT hospital episodes' },
  { key: 'appointments', label: 'Appointments' },
  { key: 'recapture_opps', label: 'Open HCC recapture opportunities' },
  { key: 'care_gaps', label: 'Open quality care gaps' },
];
const DEFAULT_GRAPH_NODES = ['claims', 'ehr_medications', 'observations', 'encounters', 'adt_episodes', 'appointments'];

export default {
  name: 'StructuredMetaSection',
  props: {
    record: { type: Object, required: true },
    artifact: { type: Object, required: true },
    context: { type: Object, default: () => ({}) },
  },
  emits: ['patch'],
  data() {
    const cfg = this.record.config || {};
    return {
      description: this.record.description || '',
      isActive: this.record.is_active !== false,
      executor: cfg.executor || 'agent',
      populationDaId: cfg.population_da_id ?? null,
      unitEntityType: cfg.unit_entity_type || '',
      model: cfg.model || 'gpt-5.6-luna',
      usePatientGraph: cfg.use_patient_graph !== false,
      graphNodes: Array.isArray(cfg.graph_nodes) ? [...cfg.graph_nodes] : [...DEFAULT_GRAPH_NODES],
      graphNodeCatalog: GRAPH_NODE_CATALOG,
      schedule: cfg.schedule || '',
      recompute: cfg.recompute || 'changed',
      contractsStr: Array.isArray(cfg.contracts) ? cfg.contracts.join(', ') : '',
      dataPackets: (Array.isArray(cfg.data_packets) ? cfg.data_packets : []).map((p) => ({
        da_id: p.da_id ?? null,
        label: p.label || '',
        description: p.description || '',
        _filtersStr: p.filters ? JSON.stringify(p.filters) : '',
        _filtersErr: null,
      })),
      dossierDAs: [],
      populations: [],
      entityTypes: [],
      hydrating: true,
    };
  },
  async mounted() {
    // Populations = role='population' DAs; entity types from the entity-types API.
    try {
      const [daRes, etRes] = await Promise.all([
        axios.get('api/impact_analytics/attributes/'),
        axios.get('api/entity-types/'),
      ]);
      const das = daRes.data.results || daRes.data || [];
      this.populations = das.filter((d) => d.role === 'population').sort((a, b) => a.name.localeCompare(b.name));
      this.dossierDAs = das.filter((d) => d.role === 'dossier').sort((a, b) => a.name.localeCompare(b.name));
      const ets = etRes.data.results || etRes.data || [];
      this.entityTypes = [...new Set(ets.map((e) => e.type_name))].sort();
    } catch { /* leave empty */ }
    this.$nextTick(() => { this.hydrating = false; });
  },
  methods: {
    emitMeta() {
      if (this.hydrating) return;
      this.$emit('patch', { description: this.description, is_active: this.isActive });
    },
    emitConfig() {
      if (this.hydrating) return;
      const config = { ...(this.record.config || {}) };
      config.population_da_id = this.populationDaId;
      if (this.executor === 'patient_dossier') {
        config.model = this.model;
        config.use_patient_graph = this.usePatientGraph;
        // Keep NODE_ORDER for stable output; only persist when using the graph.
        config.graph_nodes = this.usePatientGraph
          ? GRAPH_NODE_CATALOG.map((n) => n.key).filter((k) => this.graphNodes.includes(k))
          : [];
        config.recompute = this.recompute;
        if (this.schedule) config.schedule = this.schedule; else delete config.schedule;
        // Contracts — one scheduled run per contract; empty => single run.
        const contracts = this.contractsStr.split(',').map((c) => c.trim()).filter(Boolean);
        if (contracts.length) config.contracts = contracts; else delete config.contracts;
        // Data packets — DA context blocks. Persist valid rows (has a da_id).
        config.data_packets = this.dataPackets
          .filter((p) => p.da_id)
          .map((p) => {
            const out = { da_id: p.da_id, label: p.label || '' };
            if (p.description) out.description = p.description;
            if (p._filtersStr && p._filtersStr.trim()) {
              try { out.filters = JSON.parse(p._filtersStr); } catch { /* keep last-good: skip */ }
            }
            return out;
          });
      } else {
        config.unit_entity_type = this.unitEntityType;
      }
      this.$emit('patch', { config });
    },
    toggleNode(key, checked) {
      const set = new Set(this.graphNodes);
      if (checked) set.add(key); else set.delete(key);
      this.graphNodes = [...set];
      this.emitConfig();
    },
    addPacket() {
      this.dataPackets.push({ da_id: null, label: '', description: '', _filtersStr: '', _filtersErr: null });
    },
    removePacket(idx) {
      this.dataPackets.splice(idx, 1);
      this.emitConfig();
    },
    onFiltersInput(pkt, val) {
      pkt._filtersStr = val;
      if (val.trim()) {
        try { JSON.parse(val); pkt._filtersErr = null; }
        catch (e) { pkt._filtersErr = `Invalid JSON: ${e.message}`; return; }  // don't emit while invalid
      } else {
        pkt._filtersErr = null;
      }
      this.emitConfig();
    },
    setExecutor(mode) {
      if (this.executor === mode) return;
      this.executor = mode;
      const config = { ...(this.record.config || {}), executor: mode };
      // Seed sane defaults on first switch into dossier.
      if (mode === 'patient_dossier') {
        if (config.use_patient_graph === undefined) config.use_patient_graph = true;
        if (!config.model) config.model = 'gpt-5.6-luna';
        if (!Array.isArray(config.graph_nodes)) config.graph_nodes = [...DEFAULT_GRAPH_NODES];
      }
      this.$emit('patch', { config });
    },
  },
};
</script>
